import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import config from '../../config.js';
import LoadingSpinner from './LoadingSpinner.jsx';
import styles from '../../styles/partial/AddSong.module.css';
import { useT } from '../../contexts/LocaleContext.jsx';

import { useFetchWrapper } from '../../_helpers/fetchWrapper.js';
import { capturePerformerEvent } from '../../_helpers/performerEvents.js';
import {
  parseGenres, isNotInCatalogue, closestOf, songLookupErrorMessage,
} from '../../_helpers/manualSongs.js';

/** The fields the metadata lookup can fill. */
const METADATA_FIELDS = ['lyrics', 'chords', 'tempo', 'key'];

/** The song info a band enters for a song it adds anyway (ENG-796). */
const EMPTY_SONG_INFO = Object.freeze({
  album: '',
  releaseDate: '',
  genres: '',
  songWriter: '',
  explicit: false,
});

/**
 * The PUT /bands/{id}/songs body for "Add it anyway" (ENG-795/796). The
 * server still looks the song up first, and a match is added as the
 * catalogue song; only a genuine not-found becomes the band's own song, with
 * the song info entered here. A blank artist is the band's name on the
 * server, and song info is sent only when entered.
 */
export const addAnywayBody = (song, info) => {
  const body = {
    title: song.title.trim(),
    artist: song.artist.trim(),
    lyrics: song.lyrics,
    chords: song.chords,
    tempo: song.tempo,
    key: song.key,
    active: song.active,
    add_anyway: true,
    explicit: Boolean(info.explicit),
  };
  const album = info.album.trim();
  if (album) body.original_album = album;
  if (info.releaseDate) body.release_date = info.releaseDate;
  const songWriter = info.songWriter.trim();
  if (songWriter) body.song_writer = songWriter;
  const genres = parseGenres(info.genres);
  if (genres.length > 0) body.genres = genres;
  return body;
};

const AddSong = () => {
  const [song, setSong] = useState({
    title: '',
    artist: '',
    lyrics: '',
    chords: '',
    tempo: '',
    key: '',
    active: false,
  });

  const [artistOrTitleChanged, setArtistOrTitleChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  // Lookup values for fields that already had a different value (ENG-508):
  // offered with "Use suggested" rather than written over what was typed.
  const [suggestions, setSuggestions] = useState({});
  const songRef = useRef(song);
  songRef.current = song;
  // The add's not-found answer (ENG-796): `{ closest }`, the nearest
  // catalogue song for "Did you mean ...?" or null.
  const [notFound, setNotFound] = useState(null);
  // "Add it anyway": the song-info fields are shown and the save sends
  // add_anyway. Only ever reached from a not-found, never from a failure.
  const [addingAnyway, setAddingAnyway] = useState(false);
  const [songInfo, setSongInfo] = useState(EMPTY_SONG_INFO);
  // An add-anyway the server found after all: `{ title, artist }` as added.
  const [foundAfterAll, setFoundAfterAll] = useState(null);
  const bandNameRef = useRef(null);

  const navigate = useNavigate();
  // The band in the URL (/performers/:bandid/add-song). It used to come from
  // sessionStorage, which only PerformerHome sets: opened directly the page
  // refused to save, and opened after visiting another band's page it added
  // the song to that band's crate (ENG-459).
  const { bandid: bandId } = useParams();
  const t = useT();
  const fetchWrapper = useFetchWrapper();
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'title' || name === 'artist') {
      setArtistOrTitleChanged(true);
      // A not-found answer stays until the next save: the band may correct
      // the spelling, or clear the artist before "Add it anyway".
    }

    setSong((prevSong) => ({
      ...prevSong,
      [name]: value,
    }));
    // Typing into a field settles it; a stale suggestion for it goes away.
    if (suggestions[name] !== undefined) {
      setSuggestions(({ [name]: _dropped, ...rest }) => rest);
    }
  };

  const applySuggestion = (field) => {
    setSong((prevSong) => ({ ...prevSong, [field]: suggestions[field] }));
    setSuggestions(({ [field]: _used, ...rest }) => rest);
  };

  // The artist a song added anyway starts with: the band's name, still
  // editable. Only filled into an artist field that is still empty, so
  // nothing typed is overwritten.
  const prefillArtistWithBandName = useCallback(async () => {
    if (!bandId) return;
    try {
      if (bandNameRef.current === null) {
        const band = await fetchWrapper.get(`${config.apiBaseUrl}/public/bands/${bandId}`);
        bandNameRef.current = typeof band?.name === 'string' ? band.name : '';
      }
      const name = bandNameRef.current;
      if (name) {
        setSong((prevSong) => (prevSong.artist.trim() ? prevSong : { ...prevSong, artist: name }));
      }
    } catch {
      // A blank artist is fine here: the server uses the band's name.
      bandNameRef.current = '';
    }
  }, [bandId, fetchWrapper]);

  const handleSongInfoChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSongInfo((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  /**
   * Adds the song, always looking it up first: an ordinary add, or with
   * `addAnyway` the add-anyway that the server verifies again. `override` is
   * a title and artist to add instead of the typed ones ("Did you mean ...?").
   */
  const save = async ({ addAnyway = false, override = null } = {}) => {
    // One save at a time (ENG-508): Save is disabled while it runs, and a
    // second submit before it re-renders is ignored here.
    if (submittingRef.current) return;
    setSubmitError('');
    setNotFound(null);
    setFoundAfterAll(null);
    if (!bandId) {
      setSubmitError(t('addSong.errors.sessionExpired'));
      return;
    }
    const current = override ? { ...songRef.current, ...override } : songRef.current;
    const title = current.title.trim();
    const artist = current.artist.trim();
    if (addAnyway ? !title : !title || !artist) {
      setSubmitError(t(addAnyway ? 'addSong.errors.titleRequired' : 'addSong.errors.titleArtistRequired'));
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const body = addAnyway ? addAnywayBody(current, songInfo) : { ...current, title, artist };
      const added = await fetchWrapper.put(`${config.apiBaseUrl}/bands/${bandId}/songs`, body);
      // ENG-888: a catalogue song is "search" (an add-anyway the server found
      // after all included); one added as the band's own is "manual".
      const songId = Number(added?.id ?? added?.ID ?? added?.songId ?? added?.song_id);
      void capturePerformerEvent('songlist_song_added', {
        bandId,
        fetchWrapper,
        source: addAnyway && added?.result !== 'matched' ? 'manual' : 'search',
        ...(Number.isSafeInteger(songId) && songId > 0 && { song_id: songId }),
      });
      if (addAnyway && added?.result === 'matched') {
        // Found after all: the catalogue song was added, in its spelling.
        submittingRef.current = false;
        setSubmitting(false);
        setAddingAnyway(false);
        setFoundAfterAll({ title: added.title || title, artist: added.artist || artist });
        return;
      }
      navigate(`/performers/${bandId}/song-list`);
    } catch (error) {
      console.error('Error adding song:', error);
      submittingRef.current = false;
      setSubmitting(false);
      if (!addAnyway && isNotInCatalogue(error)) {
        setNotFound({ closest: closestOf(error) });
        return;
      }
      // Never the server's own wording, which may name the lookup's provider.
      setSubmitError(songLookupErrorMessage(error, t) || t('addSong.errors.saveFailed'));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    save({ addAnyway: addingAnyway });
  };

  // "Did you mean {title} by {artist}?": the candidate is filled in and
  // added as an ordinary add.
  const addClosest = () => {
    const closest = notFound?.closest;
    if (!closest) return;
    setSong((prevSong) => ({ ...prevSong, title: closest.title, artist: closest.artist }));
    setArtistOrTitleChanged(false);
    save({ override: closest });
  };

  // "Add it anyway": the title stays, the artist starts as the band's name
  // when blank, and the song info is shown for the band to fill in.
  const startAddAnyway = () => {
    setNotFound(null);
    setSubmitError('');
    setAddingAnyway(true);
    // The metadata lookup has nothing for a song the catalogue lacks.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortControllerRef.current) abortControllerRef.current.abort();
    setLoading(false);
    setArtistOrTitleChanged(false);
    setSuggestions({});
    prefillArtistWithBandName();
  };

  const searchForMetaData = useCallback(async (signal) => {
    setLoading(true);
    const params = new URLSearchParams({ title: song.title, artist: song.artist }).toString();
    try {
      const data = await fetchWrapper.get(`${config.apiBaseUrl}/songs/metadata?${params}`);
      if (signal.aborted) return;
      // Fill only the fields that are still empty (ENG-508). A field that
      // has a value keeps it; if the lookup has a different one, it is
      // offered beside the field instead. It used to replace all four, with
      // '' for anything the lookup lacked, wiping what had been typed.
      const current = songRef.current;
      const fills = {};
      const offered = {};
      for (const field of METADATA_FIELDS) {
        const found = data?.[field];
        if (found === undefined || found === null || String(found) === '') continue;
        const value = String(found);
        if (!String(current[field] ?? '').trim()) fills[field] = value;
        else if (String(current[field]) !== value) offered[field] = value;
      }
      setSong((prevSong) => ({ ...prevSong, ...fills }));
      setSuggestions(offered);
    } catch (error) {
      if (signal.aborted) return;
      if (error && error.status === 404) {
        console.info('No metadata found for song:', song.title, song.artist);
      } else {
        console.error('Error fetching metadata:', error);
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
        setArtistOrTitleChanged(false);
      }
    }
  }, [song.title, song.artist]);

  useEffect(() => {
    // A song being added anyway is one the catalogue has nothing for.
    if (!addingAnyway && song.title && song.artist && artistOrTitleChanged) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      debounceRef.current = setTimeout(() => {
        const controller = new AbortController();
        abortControllerRef.current = controller;
        searchForMetaData(controller.signal);
      }, 300);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [song.title, song.artist, artistOrTitleChanged, addingAnyway]);

  const closest = notFound?.closest ?? null;

  return (
    <div className={styles.page}>
      <h1 className={styles.heading}>{t('addSong.heading')}</h1>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.leftColumn}>
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="song-title">{t('addSong.titleLabel')}</label>
            <input id="song-title" data-testid="add-song-title" className={styles.input} type="text" name="title" placeholder={t('addSong.titlePlaceholder')} value={song.title} onChange={handleChange} required />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="song-artist">{t('addSong.artistLabel')}</label>
            <input id="song-artist" data-testid="add-song-artist" className={styles.input} type="text" name="artist" placeholder={t('addSong.artistPlaceholder')} value={song.artist} onChange={handleChange} required={!addingAnyway} />
          </div>
          {addingAnyway && (
            <div className={styles.songInfo} data-testid="add-song-song-info">
              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="song-album">{t('addSong.albumLabel')}</label>
                <input id="song-album" className={styles.input} type="text" name="album" maxLength={255} placeholder={t('addSong.albumPlaceholder')} value={songInfo.album} onChange={handleSongInfoChange} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="song-release-date">{t('addSong.releaseDateLabel')}</label>
                <input id="song-release-date" className={styles.input} type="date" name="releaseDate" value={songInfo.releaseDate} onChange={handleSongInfoChange} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="song-genres">{t('addSong.genresLabel')}</label>
                <input id="song-genres" className={styles.input} type="text" name="genres" placeholder={t('addSong.genresPlaceholder')} value={songInfo.genres} onChange={handleSongInfoChange} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="song-writer">{t('addSong.songWriterLabel')}</label>
                <input id="song-writer" className={styles.input} type="text" name="songWriter" placeholder={t('addSong.songWriterPlaceholder')} value={songInfo.songWriter} onChange={handleSongInfoChange} />
              </div>
              <div className={styles.checkboxGroup}>
                <input id="song-explicit" className={styles.checkbox} type="checkbox" name="explicit" checked={songInfo.explicit} onChange={handleSongInfoChange} />
                <label className={styles.checkboxLabel} htmlFor="song-explicit">{t('addSong.explicitLabel')}</label>
              </div>
            </div>
          )}
          <div className={styles.tempoKeyRow}>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="song-tempo">{t('addSong.tempoLabel')}</label>
              <input id="song-tempo" className={styles.input} type="text" name="tempo" placeholder={t('addSong.tempoPlaceholder')} value={song.tempo} onChange={handleChange} />
              {suggestions.tempo !== undefined && (
              <button type="button" className={styles.useSuggested} onClick={() => applySuggestion('tempo')} data-testid="add-song-use-suggested-tempo">
                {t('addSong.useSuggested')}
              </button>
            )}
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="song-key">{t('addSong.keyLabel')}</label>
              <input id="song-key" className={styles.input} type="text" name="key" placeholder={t('addSong.keyPlaceholder')} value={song.key} onChange={handleChange} />
              {suggestions.key !== undefined && (
              <button type="button" className={styles.useSuggested} onClick={() => applySuggestion('key')} data-testid="add-song-use-suggested-key">
                {t('addSong.useSuggested')}
              </button>
            )}
            </div>
          </div>
          {loading && (
            <div className={styles.spinnerWrap}>
              <LoadingSpinner />
            </div>
          )}
          <div className={styles.checkboxGroup}>
            <input
              id="song-active"
              className={styles.checkbox}
              type="checkbox"
              name="active"
              checked={song.active}
              onChange={(e) => setSong((prevSong) => ({ ...prevSong, active: e.target.checked }))}
            />
            <label className={styles.checkboxLabel} htmlFor="song-active">{t('addSong.publicLabel')}</label>
          </div>
          {submitError && (
            <p className={styles.errorMessage} data-testid="add-song-error" role="alert">{submitError}</p>
          )}
          {notFound && (
            <div className={styles.notFound} role="alert" data-testid="add-song-not-found">
              <p className={styles.errorMessage}>{t('addSong.notFound.message')}</p>
              {closest ? (
                <div className={styles.notFoundActions}>
                  <button type="button" className={styles.btnSave} onClick={addClosest} disabled={submitting} data-testid="add-song-did-you-mean">
                    {t('addSong.notFound.didYouMean', { title: closest.title, artist: closest.artist })}
                  </button>
                  <button type="button" className={styles.useSuggested} onClick={startAddAnyway} disabled={submitting} data-testid="add-song-add-anyway">
                    {t('addSong.notFound.addAnyway')}
                  </button>
                </div>
              ) : (
                <>
                  <p className={styles.hint}>{t('addSong.notFound.checkSpelling')}</p>
                  <button type="button" className={styles.useSuggested} onClick={startAddAnyway} disabled={submitting} data-testid="add-song-add-anyway">
                    {t('addSong.notFound.addAnyway')}
                  </button>
                </>
              )}
            </div>
          )}
          {foundAfterAll && (
            <div className={styles.notFound} role="status" data-testid="add-song-found">
              <p className={styles.hint}>{t('addSong.foundAfterAll', { title: foundAfterAll.title, artist: foundAfterAll.artist })}</p>
              <button type="button" className={styles.useSuggested} onClick={() => navigate(`/performers/${bandId}/song-list`)}>
                {t('addSong.backToSongList')}
              </button>
            </div>
          )}
          <div className={styles.spacer} />
          <button type="submit" className={styles.btnSave} data-testid="add-song-save" disabled={submitting}>{t('common.actions.save')}</button>
        </div>
        <div className={styles.rightColumn}>
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="song-lyrics">{t('addSong.lyricsLabel')}</label>
            <textarea id="song-lyrics" className={styles.textareaLyrics} name="lyrics" placeholder={t('addSong.lyricsPlaceholder')} value={song.lyrics} onChange={handleChange} />
            {suggestions.lyrics !== undefined && (
              <button type="button" className={styles.useSuggested} onClick={() => applySuggestion('lyrics')} data-testid="add-song-use-suggested-lyrics">
                {t('addSong.useSuggested')}
              </button>
            )}
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="song-chords">{t('addSong.chordsLabel')}</label>
            <textarea id="song-chords" className={styles.textareaChords} name="chords" placeholder={t('addSong.chordsPlaceholder')} value={song.chords} onChange={handleChange} />
            {suggestions.chords !== undefined && (
              <button type="button" className={styles.useSuggested} onClick={() => applySuggestion('chords')} data-testid="add-song-use-suggested-chords">
                {t('addSong.useSuggested')}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddSong;
